import { useEffect, type ReactNode } from 'react';
import { Button, Chip, Select } from '@nyce/admin-kit/ui';
import { ErrorBox, Loading } from '../../components/common';
import { useWizardParam } from './params';
import '../onboarding.css';

/**
 * One step of a wizard. Its state is derived from what the owning services report (decision 2), never stored:
 * `done` = already in place, `todo` = to do now, `optional` = may be skipped, `blocked` = needs an earlier step or a
 * scope (`blocked` says which), `waiting` = done here, waiting for something on site (agent connects, first data).
 */
export type StepState = 'done' | 'todo' | 'optional' | 'blocked' | 'waiting';

export interface WizardStep {
  id: string;
  title: string;
  state: StepState;
  /** One line under the title in the step list: what was chosen or found. */
  summary?: ReactNode;
  /** Why the step cannot be done yet. */
  blocked?: string;
  body: ReactNode;
}

const STATE_LABEL: Record<StepState, [string, string]> = {
  done: ['Done', 'green'],
  todo: ['To do', 'blue'],
  optional: ['Optional', 'grey'],
  blocked: ['Blocked', 'grey'],
  waiting: ['Waiting', 'amber'],
};

export function StepChip({ state }: { state: StepState }) {
  const [label, tone] = STATE_LABEL[state];
  return (
    <Chip size="sm" className={`oph-status nyk-tone-${tone}`} data-state={state}>
      {label}
    </Chip>
  );
}

/** The step to open when the URL names none: the first one still to do, else the last. */
export const firstOpenStep = (steps: WizardStep[]) =>
  (steps.find((s) => s.state === 'todo' || s.state === 'waiting') ?? steps[steps.length - 1])?.id ?? '';

/**
 * Steps list + the open step (`?step=`). On a phone (CSS, ≤1024px) the list folds into a "Step n of m" picker above the panel. Every
 * step can be opened (a blocked one says why); Back / Next walk the list; Re-check reloads the services' state.
 */
export function Wizard({
  steps,
  loading,
  error,
  onRecheck,
  intro,
}: {
  steps: WizardStep[];
  loading?: boolean;
  error?: string | null;
  onRecheck?: () => void;
  intro?: ReactNode;
}) {
  const [stepParam, setStep] = useWizardParam('step');
  const current = steps.find((s) => s.id === stepParam) ?? steps.find((s) => s.id === firstOpenStep(steps));
  const index = current ? steps.indexOf(current) : 0;
  const done = steps.filter((s) => s.state === 'done').length;
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function' && stepParam)
      window.scrollTo({ top: 0 });
  }, [stepParam]);

  const list = (
    <ol className="ob-steps" data-testid="wizard-steps">
      {steps.map((s, i) => (
        <li key={s.id}>
          <button
            type="button"
            className={`ob-step${s.id === current?.id ? ' ob-step-on' : ''}`}
            data-step={s.id}
            data-state={s.state}
            aria-current={s.id === current?.id ? 'step' : undefined}
            onClick={() => setStep(s.id)}
          >
            <span className={`ob-step-no ob-step-no-${s.state}`} aria-hidden="true">
              {s.state === 'done' ? '✓' : i + 1}
            </span>
            <span className="ob-step-text">
              <span className="ob-step-title">{s.title}</span>
              {s.summary && <span className="ob-step-summary">{s.summary}</span>}
            </span>
          </button>
        </li>
      ))}
    </ol>
  );

  return (
    <div className="ob-wizard" data-testid="wizard">
      {intro}
      <ErrorBox error={error} />
      <div className="ob-wizard-grid">
        <div className="ob-compact-head">
          <Select
            label={`Step ${index + 1} of ${steps.length} · ${done} done`}
            value={current?.id ?? ''}
            onChange={setStep}
            searchable={false}
            options={steps.map((s, i) => ({
              value: s.id,
              label: `${i + 1}. ${s.title}${s.state === 'done' ? ' ✓' : ''}`,
            }))}
          />
        </div>
        <aside className="ob-side">
          <div className="ob-progress">
            <span className="nyk-ui-eyebrow">
              {done} of {steps.length} done
            </span>
            <div className="oph-meter-track">
              <div
                className="oph-meter-fill"
                style={{ width: `${(100 * done) / Math.max(1, steps.length)}%` }}
              />
            </div>
          </div>
          {list}
        </aside>
        <section className="nyk-card ob-panel" data-testid="wizard-panel" data-step={current?.id}>
          {current && (
            <>
              <div className="ob-panel-head">
                <h2>
                  <span className="ob-panel-no">{index + 1}.</span> {current.title}
                </h2>
                <StepChip state={current.state} />
              </div>
              {current.state === 'blocked' && current.blocked && (
                <div className="nyk-notice nyk-notice-warn" data-testid="step-blocked">
                  {current.blocked}
                </div>
              )}
              <Loading loading={!!loading} />
              <div className="ob-panel-body">{current.body}</div>
              <div className="ob-panel-foot">
                <Button variant="ghost" disabled={index === 0} onClick={() => setStep(steps[index - 1].id)}>
                  Back
                </Button>
                {onRecheck && (
                  <Button variant="ghost" onClick={onRecheck} data-testid="recheck">
                    Re-check
                  </Button>
                )}
                <Button
                  variant={current.state === 'done' ? 'primary' : 'secondary'}
                  disabled={index === steps.length - 1}
                  onClick={() => setStep(steps[index + 1].id)}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
